import { useState, useEffect,useRef, useCallback} from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
} from "@material-ui/core";
import { LiveHelp, Restore, TrendingUp, Menu, ExitToApp, Forum, Brightness2, Brightness4, CreditCard, Loyalty, AttachMoney, InsertLink, Settings, CardGiftcard, AssignmentTurnedIn, Assessment, Compare, PlaylistAddCheck, People, Publish } from "@material-ui/icons";
import { CONSTANTS } from "../../constants/app-constant";
import ShareEarnModal from "../../components/share-earn-modal/ShareEarnModal";
import { INVITE_CAMPAIGN } from "../../utils/invite-link";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPreference, setPreference, menuOpenStatus } from "../../actions";
import mixpanel from 'mixpanel-browser';
import { applyThemeVars } from '../../constants/themes';
import { resolveInitialTheme, syncThemeBodyClass } from '../../utils/theme-preference';
import { HIDE_ABSITE_DIAGNOSTIC } from '../../constants/incidentFlags';
import { NAV_BOTTOM_TABS } from '../../constants/navFlags';
import { NAV_SURFACE, MORE_SHEET_OPENED_EVENT } from '../../constants/navSurface';
import BottomTabBar from '../../components/bottom-tab-bar/bottom-tab-bar';
import { isInstitutionalPromotionEnabled } from '../../components/institutional/institutional-flags';
import { clearLocalStorageKeepingRecordingChoice } from '../../utils/replay-consent';
import { referralRewardsAvailable } from '../../utils/native-referral-policy';
const logo = require("../../images/logo_dark.png").default;

// 224px: wide enough for the longest label ("ABSITE DIAGNOSTIC" at 12px
// uppercase + 41px icon) — at 190 the drawer clipped labels mid-word.
const drawerWidth = 224;
const baseMenuItems = [
  { name: "Create Quiz", route: "/create-quiz", icon: () => <LiveHelp /> },
  ...(HIDE_ABSITE_DIAGNOSTIC
    ? []
    : [{ name: "ABSITE Diagnostic", route: "/absite-diagnostic", icon: () => <AssignmentTurnedIn /> }]),
  { name: "Quiz History", route: "/quiz-history", icon: () => <Restore /> },
  {
    name: "Performance",
    route: "/overall-performance",
    icon: () => <TrendingUp />,
  },
  {
    name: "Subscriptions",
    route: "/subscriptions",
    icon: () => <CreditCard />,
  },
  {
    name: "Settings",
    route: "/user-settings",
    icon: () => <Settings />,
  }
];

const adminMenuItems = [
  {
    name: "Feedbacks",
    route: "/feedback-dashboard",
    icon: () => <Forum />
  },
  {
    name: "Manage Plans",
    route: "/manage-plans",
    icon: () => <Loyalty />
  },
  {
    name: "Refund",
    route: "/refund",
    icon: () => <AttachMoney />
  },
  {
    name: "QLink",
    route: "/qlink",
    icon: () => <InsertLink />
  },
  {
    name: "Publish Console",
    route: "/admin/review-publish",
    icon: () => <Publish />
  },
  {
    name: "Program Metrics",
    route: "/program-metrics",
    icon: () => <Assessment />
  }
];

// Reviewer surfaces visible to any tutor OR admin.
const tutorAdminMenuItems = [
  {
    name: "Image Review",
    route: "/admin/review-queue",
    icon: () => <Compare />
  },
  {
    name: "Review Status",
    route: "/admin/review-status",
    icon: () => <PlaylistAddCheck />
  }
];

const institutionalRewardsMenuItem = {
  name: "Referral Rewards",
  route: "/admin/institutional-rewards",
  icon: () => <CardGiftcard />
};

const shareEarnMenuItem = {
  name: "Share & Earn",
  route: "share-earn",
  icon: () => <CardGiftcard />
};

// No "Privacy choices" entry (owner request, 2026-10-28): the session-
// recording choice lives in Settings > "Help improve QLearn", which the
// Settings entry above already reaches.

export const getMenuItemsForRole = (role) => {
  let menuItems = [...baseMenuItems];

  // Flag-gated tutor<->student mentorships, visible to every role when on.
  // Role-aware label matches the /mentorships page; grouped after Performance.
  if (CONSTANTS.mentorshipsEnabled) {
    const afterPerf = menuItems.findIndex((m) => m.route === "/overall-performance") + 1;
    menuItems.splice(afterPerf, 0, {
      name: role === "ROLE_TUTOR" ? "My Students" : "My Tutors",
      route: "/mentorships",
      icon: () => <People />,
    });
  }

  if (role === 'ROLE_ADMIN') {
    menuItems = [...menuItems, ...adminMenuItems];
    // Dark institutional reward queue (manual fulfillment); admin-only here,
    // and the server authorizes every request independently.
    if (isInstitutionalPromotionEnabled() && referralRewardsAvailable()) {
      menuItems.push(institutionalRewardsMenuItem);
    }
  }

  if (role === 'ROLE_ADMIN' || role === 'ROLE_TUTOR') {
    menuItems = [...menuItems, ...tutorAdminMenuItems];
    return menuItems.filter(m => m.route !== "/subscriptions");
  }

  // iOS ruling B5: no Share & Earn entry in the native app.
  return referralRewardsAvailable() ? [...menuItems, shareEarnMenuItem] : menuItems;
};

export default function MenuView() {
  const history = useHistory();
  const theme = useTheme();
  const location = useLocation();
  const currentRoute = location.pathname;
  const [menuItems, setMenuItems] = useState(baseMenuItems);
  const [currentTheme, setCurrentTheme] = useState('light');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const dispatch = useDispatch();
  const drawerRef = useRef(null);

  const userPreferenceData = useSelector((state) => {
    return state.setUserPreference?.preference || state.getUserPreference?.preference;
  });

  const userDetailsFromToken = useSelector((state) => {
    return state.userDetailsFromToken?.userDetails;
  });

  const menuStatus = useSelector((state) => {
    return state.menuOpenStatus?.open;
  });

  const [open, setOpen] = useState(menuStatus);
  // On phone viewports the drawer is an in-flow rail that covers ~half the
  // screen when open; collapse it after navigating so it doesn't obscure the
  // destination page (desktop keeps the persistent expanded sidebar).
  const isMobile = useMediaQuery(theme.breakpoints.down(600));

  const setMenuOnRole = (role) => {
    setMenuItems(getMenuItemsForRole(role));
  }

  useEffect(() => {
    let role = window.localStorage.getItem("role");

    if (role) {
      setMenuOnRole(role);
    } else {
      setTimeout(() => {
        role = window.localStorage.getItem("role");
        setMenuOnRole(role);
      }, 500);
    }

  }, []);

  useEffect(() => {
    if (menuStatus !== undefined && open !== menuStatus) {
      setOpen(menuStatus);
    }
  }, [menuStatus, open]);

  useEffect(() => {
    const id = window.localStorage.getItem("userId") || userDetailsFromToken?.id;

    if (userPreferenceData === undefined && id && currentRoute !== '/create-quiz') {
      dispatch(getPreference(id));
    } else if (id) {
      // S6: saved preference wins; otherwise follow the OS appearance.
      const newTheme = resolveInitialTheme(userPreferenceData);
      setCurrentTheme(newTheme);
      setTheme(newTheme);
    }
  }, [dispatch, userPreferenceData, userDetailsFromToken, currentRoute]);

  const handleClickOutside = useCallback((event) => {
    if (open && drawerRef.current && !drawerRef.current.contains(event.target)) {
      setOpen(false);
      dispatch(menuOpenStatus({ open: false }));
    }
  }, [open, dispatch]);
  
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);
  

  const toggleDrawerOpen = () => {
    setOpen(!open);
    dispatch(menuOpenStatus({ open: !open }));
  };

  const goToHome = () => {
    history.push('/create-quiz');
  };

  const onThemeChange = () => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    setCurrentTheme(newTheme);
    setTheme(newTheme);
    const userId = window.localStorage.getItem("userId");
    dispatch(setPreference({ userId, body: { darkTheme: newTheme === 'dark' } }));
  };

  const setTheme = (newTheme) => {
    // applyThemeVars sets the flag-selected value-set, UNSETS keys absent from it (B5:
    // in-document flag-OFF reversibility), and keeps data-skin in lockstep with the flag.
    applyThemeVars(newTheme);
    // S6: keep the round-1 design tokens' dark values in sync.
    syncThemeBodyClass(newTheme);
  }

  // Which chrome the selection came from. The rail and the bottom tab bar route
  // through this ONE handler, so without a discriminator every destination event
  // looks identical across the two navs — you can see that a student reached
  // Subscriptions, but not whether they tapped a tab or dug it out of the More
  // overflow. Subscriptions and Share & Earn are exactly the destinations the R2
  // rebuild moves into that overflow, so this is the property the flip has to be
  // read with. Default 'rail' keeps every existing (unargumented) caller correct.
  const onMenuSelect = (route, navSurface = NAV_SURFACE.RAIL) => {
    const userinfos = JSON.parse(localStorage.getItem('userdetails'));
    const userrole = window.localStorage.getItem("role");
    if (route === "share-earn") {
      // Share & Earn emitted NOTHING before this: the early return sits above
      // the track call, and the generic name below is `route.slice(1)`, which
      // assumes a leading slash — "share-earn" would have become "hare-earn",
      // which is presumably why it was excluded rather than fixed. So it gets
      // an explicit event name, exactly like the other non-path route
      // ("logout" -> Menu-sign-out).
      //
      // This matters here specifically: Share & Earn is one of the TWO
      // destinations the R2 rebuild moves off the persistent rail into the More
      // overflow. Without this, half of what the surface discriminator exists to
      // diagnose would still be invisible after the flip — referral reach would
      // look flat because nothing was ever counted, not because nothing changed.
      mixpanel.track("Menu-share-earn", {
        $name: userinfos?.name,
        email: userinfos?.email,
        role: userrole,
        navSurface
      });
      setShareModalOpen(true);
      return;
    }

    if (route === "logout") {
      dispatch({
        type: "LOGOUT",
      });
      mixpanel.track("Menu-sign-out", {
        $name: userinfos?.name,
        email: userinfos?.email,
        role: userrole,
        navSurface
      });
      // Everything but the browser's session-recording choice ("we then
      // remember your choice in your browser"): a refusal survives sign-out.
      clearLocalStorageKeepingRecordingChoice();
      history.push('/login');
      return;
    }

    const menuuserinfosstring = route.slice(1);
    mixpanel.track("Menu-" + menuuserinfosstring, {
      $name: userinfos?.name,
      email: userinfos?.email,
      role: userrole,
      navSurface
    });

    history.push(route);

    if (isMobile && open) {
      setOpen(false);
      dispatch(menuOpenStatus({ open: false }));
    }
  };

  // More-sheet opens are tracked separately from the destination events: an open
  // that ends in no selection is the tell that a student went looking for
  // something the four primary tabs do not carry. Lives here, not in the tab bar,
  // so mixpanel stays in ONE place (bottom-tab-bar.jsx stays presentational).
  const onMoreSheetOpen = () => {
    const userinfos = JSON.parse(localStorage.getItem('userdetails'));
    mixpanel.track(MORE_SHEET_OPENED_EVENT, {
      $name: userinfos?.name,
      email: userinfos?.email,
      role: window.localStorage.getItem("role")
    });
  };

  const useStyles = makeStyles(() => ({
    active: {
      // S2: restrained selected-nav state (was a saturated cyan #12aade block
      // fill) — a subtle translucent overlay on the dark chrome rail.
      backgroundColor: "var(--nav-active-bg)",
    },
    menuButton: {
      marginRight: 36,
    },
    hide: {
      display: "none",
    },
    drawer: {
      flexShrink: 0,
      whiteSpace: "nowrap",
    },
    drawerOpen: {
      // S2: single primary chrome (was #232384); desaturates in dark (S6).
      backgroundColor: "var(--chrome-primary)",
      color: "var(--on-chrome-primary)",
      // S1: rail content clears the iOS status bar / Dynamic Island.
      paddingTop: "env(safe-area-inset-top, 0px)",
      width: drawerWidth,
      // X only: the menu must stay vertically scrollable so a long menu plus
      // the flex-pinned bottom actions never clip or overlap.
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      backgroundColor: "var(--chrome-primary)",
      color: "var(--on-chrome-primary)",
      paddingTop: "env(safe-area-inset-top, 0px)",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: "hidden",
      width: theme.spacing(7) + 1,
      [theme.breakpoints.down(600)]: {
        width: '40px'
      }
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0, 1),
      [theme.breakpoints.down(600)]: {
        padding: 0
      }
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    icon: {
      color: "var(--on-chrome-primary)",
      minWidth: 41,
      [theme.breakpoints.down(600)]: {
        paddingLeft: 6,
        paddingRight: 6,
      }
    },
    itemText: {
      fontSize: 12,
      // S4: sentence-case chrome (was uppercase).
      textTransform: 'none'
    },
    logout: {
      // Bottom-pinned via flex (the Drawer paper is a flex column), NOT
      // position:absolute — an absolutely-pinned list overlaps the last menu
      // items once the role's menu grows taller than the viewport gap
      // (admin + mentorships put "Review Status" under "Dark Theme").
      marginTop: "auto",
      cursor: "pointer",
    },
    menuLogo: {
      width: '90px',
      height: '60px',
      marginLeft: -15,
      cursor: 'pointer',
      [theme.breakpoints.down(600)]: {
        width: '50px',
        height: '40px',
        marginLeft: -4
      }
    },
    menuBtn: {
      [theme.breakpoints.down(600)]: {
        paddingLeft: 3,
        paddingRight: 3,
      }
    }
  }));
  const classes = useStyles();

  // design-uplift R2 (tranche 1): on authenticated routes the desktop icon-rail
  // is REPLACED by a native iOS bottom tab bar behind the dark-ship flag. All
  // navigation/theme logic stays here; the tab bar is presentational. Flag OFF
  // (default) keeps the rail byte-for-byte, so the swap is fully reversible and
  // the rail is preserved until the R2 critic panel proves parity.
  if (NAV_BOTTOM_TABS) {
    return (
      <>
        <BottomTabBar
          items={menuItems}
          currentRoute={currentRoute}
          onSelect={onMenuSelect}
          onMoreOpen={onMoreSheetOpen}
          currentTheme={currentTheme}
          onThemeChange={onThemeChange}
        />
        <ShareEarnModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} campaign={INVITE_CAMPAIGN.SHARE_EARN_MENU} />
      </>
    );
  }

  return (
    <>
    <Drawer
      ref={drawerRef}
      data-testid="menu-drawer"
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      <img 
        alt="logo" 
        src={logo} 
        className={classes.menuLogo}
        data-testid="menu-logo"
        onClick={goToHome}
      />

      <div className={classes.toolbar}>
        <IconButton 
          onClick={toggleDrawerOpen} 
          className={classes.icon}
          data-testid="menu-toggle-button"
        >
          <Menu />
        </IconButton>
      </div>
      <List data-testid="menu-items-list">
        {menuItems.map((item, index) => (
          <ListItem
            button
            key={index}
            data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => onMenuSelect(item.route)}
            className={clsx({
              [classes.active]: item.route === currentRoute,
            }, classes.menuBtn)}
          >
            {/* Collapsed rail hides the label (classes.itemText), so a
                tooltip is the only way to tell the icon-only items apart. */}
            <Tooltip title={item.name} placement="right">
              <ListItemIcon className={classes.icon}>{item.icon()}</ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={item.name}
              className={classes.itemText}
              data-testid={`menu-item-text-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            />
          </ListItem>
        ))}
      </List>

      <List className={classes.logout} data-testid="menu-bottom-actions">
        <ListItem
          button
          onClick={() => onThemeChange()}
          className={classes.menuBtn}
          data-testid="theme-toggle-button"
        >
          <ListItemIcon className={classes.icon}>
            {currentTheme === 'light' && <Brightness2 />}
            {currentTheme !== 'light' && <Brightness4 />}
          </ListItemIcon>
          <ListItemText 
            primary={currentTheme === 'light' ? 'Dark Theme' : 'Light Theme'} 
            className={classes.itemText}
            data-testid="theme-toggle-text"
          />
        </ListItem>
        <ListItem 
          onClick={() => onMenuSelect("logout")}
          className={classes.menuBtn}
          data-testid="logout-button"
        >
          <ListItemIcon className={classes.icon}>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText 
            className={classes.itemText} 
            primary="Sign out"
            data-testid="logout-text"
          />
        </ListItem>
      </List>
    </Drawer>

      <ShareEarnModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} campaign={INVITE_CAMPAIGN.SHARE_EARN_MENU} />
    </>
  );
}
